import useRolePermission from "entities/auth/hooks/useRolePermission";
import { useUpdateGameStatusQuery } from "entities/game/queries/useUpdateGameStatusQuery";
import { isUndefined } from "lodash";
import React, { FC, SyntheticEvent } from "react";
import Drawer from "shared/components/Drawer";
import { COLOR_PRIMARY_MAIN } from "shared/theme/colors";
import StatusChip from "../../components/StatusChip";
import { useGamesPage } from "../../context";
import ActionButton from "./components/ActionButton";
import { GameDetailsProvider, useGameDetails } from "./context";
import { GameTabsEnum } from "./context/types";
import { StyledTab, StyledTabs } from "./styled";
import GameImages from "./tabs/GameImages";
import General from "./tabs/General";
import Metadata from "./tabs/Metadata";
import Tags from "./tabs/Tags";
import Translations from "./tabs/Translations";

const GameDetailsContainer: FC = () => (
  <GameDetailsProvider>
    <GameDetails />
  </GameDetailsProvider>
);

const GameDetails: FC = () => {
  const { gameDetailsDrawer } = useGamesPage();
  const { tab, setTab, gameDetails: game, onCloseDrawer } = useGameDetails();
  const { isPending: isLoadingStatus } = useUpdateGameStatusQuery();

  const { allowed: hasReadAccess } = useRolePermission("games:read");
  const { allowed: hasReadTranslationsAccess } = useRolePermission(
    "games:translations:read",
  );

  const shouldRenderAllTabs = hasReadAccess;
  const shouldRenderTranslationsTab =
    hasReadAccess || hasReadTranslationsAccess;

  const handleChangeTab = (_event: SyntheticEvent, newValue: GameTabsEnum) => {
    setTab(newValue);
  };

  return (
    <Drawer
      title={game?.displayName}
      isOpen={gameDetailsDrawer.isOpen}
      onCloseDrawer={onCloseDrawer}
      width={640}
      headingBottom={
        <StyledTabs
          value={tab}
          onChange={handleChangeTab}
          TabIndicatorProps={{
            style: {
              backgroundColor: COLOR_PRIMARY_MAIN,
              height: 2,
            },
          }}
        >
          {shouldRenderAllTabs && [
            <StyledTab
              key="general"
              disableRipple
              label="General"
              value={GameTabsEnum.GENERAL}
              id="simple-tab-general"
              aria-controls="simple-tabpanel-general"
            />,
            <StyledTab
              key="images"
              disableRipple
              label="Images"
              value={GameTabsEnum.IMAGES}
              id="simple-tab-images"
              aria-controls="simple-tabpanel-images"
            />,
            <StyledTab
              key="tags"
              disableRipple
              label="Tags"
              value={GameTabsEnum.TAGS}
              id="simple-tab-tags"
              aria-controls="simple-tabpanel-tags"
            />,
            <StyledTab
              disableRipple
              label="Metadata"
              value={GameTabsEnum.METADATA}
              id="simple-tab-metadata"
              aria-controls="simple-tabpanel-metadata"
            />,
          ]}
          {shouldRenderTranslationsTab && (
            <StyledTab
              disableRipple
              label="Translations"
              value={GameTabsEnum.TRANSLATIONS}
              id="simple-tab-translations"
              aria-controls="simple-tabpanel-translations"
            />
          )}
        </StyledTabs>
      }
      headingChip={
        !isUndefined(game?.isActive) ? (
          <StatusChip
            status={Boolean(game?.isActive)}
            isLoading={isLoadingStatus}
          />
        ) : null
      }
      headingRight={<ActionButton />}
    >
      <>
        {tab === GameTabsEnum.GENERAL && <General />}
        {tab === GameTabsEnum.IMAGES && <GameImages />}
        {tab === GameTabsEnum.TAGS && <Tags />}
        {tab === GameTabsEnum.TRANSLATIONS && <Translations />}
        {tab === GameTabsEnum.METADATA && <Metadata />}
      </>
    </Drawer>
  );
};

export default GameDetailsContainer;
